import { useCallback, useEffect, useState } from "react"
import { useStageFunnel } from "../context/StageFunnelContext"
import { stageFunnelService } from "../services/stageFunnelService"

/**
 * Config catalogue du funnel Stage — GET /stage-funnel/{stateId} (template,
 * variantes en vente, horaires, dates de départ offertes, FAQ). Source UNIQUE
 * des prix / horaires / dates côté front : aucun écran ne porte de constante.
 *
 * Fetch par écran (réponse peu coûteuse, même pattern que la config été) ; le
 * nom du template est synchronisé dans le contexte (affiché par les écrans qui
 * n'ont pas besoin du reste de la config).
 *
 * `failed` + `retry` : un fetch en échec doit être VISIBLE (l'écran affiche un
 * message + un bouton réessayer) — jamais avalé en configurateur vide.
 */
export const useStageFunnelConfig = (stateId) => {
    const { state, update } = useStageFunnel()
    const [config, setConfig] = useState(null)
    const [loading, setLoading] = useState(false)
    const [failed, setFailed] = useState(false)
    const [attempt, setAttempt] = useState(0)

    const retry = useCallback(() => setAttempt((a) => a + 1), [])

    useEffect(() => {
        const id = Number(stateId)
        if (!Number.isInteger(id) || id <= 0) return
        let cancelled = false
        setLoading(true)
        setFailed(false)
        stageFunnelService
            .getStageFunnelState({ stateId: id })
            .then((res) => {
                if (cancelled) return
                const payload = res?.data?.data ?? res?.data ?? {}
                const conf = payload.config ?? payload
                setConfig(conf ?? null)
                // Propage le nom du stage (et le slug) dans le contexte — c'est
                // le catalogue qui nomme le stage, jamais le code.
                const templateName =
                    conf?.template?.name ?? conf?.template_name ?? null
                const templateSlug =
                    conf?.template?.slug ?? conf?.template_slug ?? null
                const patch = {}
                if (templateName && templateName !== state.templateName) {
                    patch.templateName = templateName
                }
                if (templateSlug && templateSlug !== state.templateSlug) {
                    patch.templateSlug = templateSlug
                }
                if (Object.keys(patch).length) update(patch)
            })
            .catch(() => {
                if (!cancelled) setFailed(true)
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateId, attempt])

    return { config, loading, failed, retry }
}
